import dayjs from "dayjs";
import { Team } from "../../../models/Team";
import TeamInfo from "../../../teamInfo";
import { teamAvatarUrl } from "../../Team/teamAvatar";

const IMAGE_FETCH_TIMEOUT_MS = 5000;

const imageType = (bytes: Uint8Array): string | null => {
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return "image/png";
    if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return "image/jpeg";
    return null;
};

// react-pdf has no timeout on the images it loads and can fail the whole document over one, so images from other hosts
// (team logos) are fetched up front and embedded; one that can't be fetched, or isn't a PNG or JPEG (all react-pdf
// draws), comes back null and is just left off.
export const embeddedImage = async (url: string | null | undefined): Promise<string | null> => {
    if (!url) return null;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), IMAGE_FETCH_TIMEOUT_MS);
    try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) return null;
        const blob = await response.blob();
        // Typed by its first bytes, like react-pdf does, as the served Content-Type isn't always an image type.
        const type = imageType(new Uint8Array(await blob.slice(0, 4).arrayBuffer()));
        if (!type) return null;
        return await new Promise<string | null>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : null);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(new Blob([blob], { type }));
        });
    } catch {
        return null;
    } finally {
        window.clearTimeout(timer);
    }
};

// The box around an image's content, leaving out the transparent or near-white margin around it. Null when there's no
// content at all.
export const imageContentBounds = (pixels: Uint8ClampedArray, width: number, height: number) => {
    let left = width;
    let top = height;
    let right = -1;
    let bottom = -1;
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
            const i = (y * width + x) * 4;
            const blank = pixels[i + 3] < 16 || (pixels[i] > 240 && pixels[i + 1] > 240 && pixels[i + 2] > 240);
            if (blank) continue;
            left = Math.min(left, x);
            right = Math.max(right, x);
            top = Math.min(top, y);
            bottom = Math.max(bottom, y);
        }
    }
    return right < 0 ? null : { left, top, width: right - left + 1, height: bottom - top + 1 };
};

// Crops away the margin around an embedded image's content, so a logo fills the space it's given rather than leaving
// its file's padding showing. Comes back unchanged when there's nothing to crop or it can't be drawn.
const trimmedImage = (src: string) =>
    new Promise<string>((resolve) => {
        const image = new Image();
        image.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = image.naturalWidth;
            canvas.height = image.naturalHeight;
            const context = canvas.getContext("2d");
            if (!context || !canvas.width || !canvas.height) return resolve(src);
            context.drawImage(image, 0, 0);
            const bounds = imageContentBounds(
                context.getImageData(0, 0, canvas.width, canvas.height).data,
                canvas.width,
                canvas.height
            );
            if (!bounds || (bounds.width === canvas.width && bounds.height === canvas.height)) return resolve(src);
            const cropped = document.createElement("canvas");
            cropped.width = bounds.width;
            cropped.height = bounds.height;
            const croppedContext = cropped.getContext("2d");
            if (!croppedContext) return resolve(src);
            croppedContext.drawImage(
                canvas,
                bounds.left,
                bounds.top,
                bounds.width,
                bounds.height,
                0,
                0,
                bounds.width,
                bounds.height
            );
            resolve(cropped.toDataURL("image/png"));
        };
        image.onerror = () => resolve(src);
        image.src = src;
    });

// A team's avatar (see TeamAvatar), embedded and cropped to its content for a PDF, or null to leave it off.
export const embeddedTeamLogo = async (team: TeamInfo | Team | null | undefined): Promise<string | null> => {
    const image = await embeddedImage(teamAvatarUrl(team));
    return image ? trimmedImage(image) : null;
};

export const pdfFileName = (name: string, date: dayjs.Dayjs) =>
    `${
        name
            .replace(/[^\w\- ]+/g, "")
            .trim()
            .replace(/\s+/g, "_") || "export"
    }_${date.format("YYYY-MM-DD")}.pdf`;

export const downloadBlob = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 10000);
};
